import { useEffect, useRef, useState } from 'react';
import { clsx } from 'clsx';
import type { TemplateConfig } from '../../services/pdf';

/**
 * Get display features for a template based on its theme
 */
function getTemplateFeatures(template: TemplateConfig): string[] {
  const features: string[] = [];

  switch (template.theme) {
    case 'classic':
      features.push('Serif fonts', '2-3 pages', 'Decorative style');
      break;
    case 'compact':
      features.push('Dense layout', '1-2 pages', 'Minimal ink');
      break;
    case 'arcana':
      features.push('Ignus theme', 'Fantasy style', 'Multi-page');
      break;
    case 'dnd-official':
      features.push('Fillable PDF', 'WotC layout', 'Official');
      break;
    default:
      features.push('Standard layout');
  }

  return features;
}

/**
 * Thumbnail preview component for different template themes
 */
function TemplateThumbnail({ theme }: { theme: string }) {
  // SVG thumbnail representing the template style
  switch (theme) {
    case 'classic':
      return (
        <svg viewBox="0 0 96 128" className="w-full h-full">
          <rect width="96" height="128" fill="#fffef9" />
          <rect x="8" y="8" width="80" height="4" fill="#8b6914" opacity="0.7" />
          <rect x="8" y="16" width="50" height="3" fill="#2d2520" opacity="0.3" />
          <rect x="8" y="24" width="80" height="1" fill="#d4a64a" />

          {/* Card outlines */}
          <rect x="8" y="28" width="37" height="28" rx="2" fill="none" stroke="#d4a64a" strokeWidth="1.5" />
          <rect x="51" y="28" width="37" height="28" rx="2" fill="none" stroke="#d4a64a" strokeWidth="1.5" />
          <rect x="8" y="60" width="37" height="28" rx="2" fill="none" stroke="#d4a64a" strokeWidth="1.5" />
          <rect x="51" y="60" width="37" height="28" rx="2" fill="none" stroke="#d4a64a" strokeWidth="1.5" />

          {/* Text lines */}
          <rect x="12" y="32" width="25" height="1.5" fill="#8b6914" opacity="0.4" />
          <rect x="12" y="38" width="20" height="1.5" fill="#2d2520" opacity="0.3" />
          <rect x="12" y="42" width="20" height="1.5" fill="#2d2520" opacity="0.3" />

          {/* Bottom border decoration */}
          <rect x="8" y="120" width="80" height="1" fill="#d4a64a" />
        </svg>
      );

    case 'compact':
      return (
        <svg viewBox="0 0 96 128" className="w-full h-full">
          <rect width="96" height="128" fill="white" />
          <rect x="6" y="6" width="84" height="2" fill="#1a1816" />

          {/* Dense horizontal bar */}
          <rect x="6" y="12" width="84" height="8" fill="#1a1816" />

          {/* Compact sections */}
          <rect x="6" y="24" width="40" height="20" fill="#fafafa" stroke="#e0e0e0" strokeWidth="1" />
          <rect x="50" y="24" width="40" height="20" fill="#fafafa" stroke="#e0e0e0" strokeWidth="1" />
          <rect x="6" y="48" width="84" height="16" fill="#fafafa" stroke="#e0e0e0" strokeWidth="1" />
          <rect x="6" y="68" width="84" height="16" fill="#fafafa" stroke="#e0e0e0" strokeWidth="1" />

          {/* Dense text lines */}
          <rect x="10" y="28" width="30" height="1" fill="#1a1816" opacity="0.4" />
          <rect x="10" y="32" width="25" height="1" fill="#1a1816" opacity="0.3" />
          <rect x="10" y="36" width="28" height="1" fill="#1a1816" opacity="0.3" />

          {/* Pills/badges */}
          <rect x="10" y="52" width="12" height="3" rx="1.5" fill="#1a1816" opacity="0.4" />
          <rect x="24" y="52" width="12" height="3" rx="1.5" fill="#1a1816" opacity="0.4" />
        </svg>
      );

    case 'arcana':
      return (
        <svg viewBox="0 0 96 128" className="w-full h-full">
          <rect width="96" height="128" fill="#f8f7f4" />
          {/* Decorative border */}
          <rect x="4" y="4" width="88" height="120" fill="none" stroke="#d4a64a" strokeWidth="2" />
          <rect x="6" y="6" width="84" height="116" fill="none" stroke="#8b6914" strokeWidth="1" />

          {/* Header */}
          <rect x="12" y="12" width="72" height="3" fill="#8b6914" opacity="0.6" />
          <rect x="12" y="18" width="50" height="2" fill="#2d2520" opacity="0.4" />

          {/* Ornamental sections */}
          <circle cx="48" cy="30" r="8" fill="none" stroke="#d4a64a" strokeWidth="1.5" />
          <rect x="12" y="42" width="72" height="20" rx="3" fill="none" stroke="#d4a64a" strokeWidth="1.5" />
          <rect x="12" y="66" width="34" height="24" rx="3" fill="none" stroke="#d4a64a" strokeWidth="1.5" />
          <rect x="50" y="66" width="34" height="24" rx="3" fill="none" stroke="#d4a64a" strokeWidth="1.5" />
        </svg>
      );

    default:
      // Generic document icon
      return (
        <svg viewBox="0 0 96 128" className="w-full h-full">
          <rect width="96" height="128" fill="white" stroke="#ccc" strokeWidth="2" />
          <rect x="12" y="16" width="72" height="4" fill="#ddd" />
          <rect x="12" y="28" width="60" height="3" fill="#eee" />
          <rect x="12" y="36" width="60" height="3" fill="#eee" />
          <rect x="12" y="44" width="60" height="3" fill="#eee" />
          <rect x="12" y="56" width="72" height="20" fill="#f5f5f5" stroke="#ddd" strokeWidth="1" />
          <rect x="12" y="84" width="72" height="20" fill="#f5f5f5" stroke="#ddd" strokeWidth="1" />
        </svg>
      );
  }
}

interface ExportPdfDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onExport: (templateId: string) => Promise<void>;
  availableTemplates: TemplateConfig[];
  defaultTemplate: TemplateConfig | undefined;
  isGenerating: boolean;
  characterName: string;
}

export function ExportPdfDialog({
  isOpen,
  onClose,
  onExport,
  availableTemplates,
  defaultTemplate,
  isGenerating,
  characterName,
}: ExportPdfDialogProps) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>(
    defaultTemplate?.id || availableTemplates[0]?.id || ''
  );

  // Update selected template when default changes (e.g., ruleset switch)
  useEffect(() => {
    if (defaultTemplate) {
      setSelectedTemplateId(defaultTemplate.id);
    }
  }, [defaultTemplate]);

  // Close on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isGenerating) onClose();
    };
    if (isOpen) {
      window.addEventListener('keydown', handleEscape);
      return () => window.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen, onClose, isGenerating]);

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dialogRef.current && !dialogRef.current.contains(e.target as Node) && !isGenerating) {
        onClose();
      }
    };
    if (isOpen) {
      const timer = setTimeout(() => {
        window.addEventListener('click', handleClickOutside);
      }, 0);
      return () => {
        clearTimeout(timer);
        window.removeEventListener('click', handleClickOutside);
      };
    }
  }, [isOpen, onClose, isGenerating]);

  const handleExport = async () => {
    await onExport(selectedTemplateId);
    onClose();
  };

  if (!isOpen) return null;

  const selectedTemplate = availableTemplates.find(t => t.id === selectedTemplateId);

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-[60] bg-black/60 backdrop-blur-sm animate-fade-in"
        aria-hidden="true"
      />

      {/* Dialog */}
      <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
        <div
          ref={dialogRef}
          className={clsx(
            'w-full max-w-2xl bg-background-card rounded-xl border border-border-default',
            'shadow-2xl animate-scale-in'
          )}
          role="dialog"
          aria-modal="true"
          aria-labelledby="export-dialog-title"
        >
          {/* Header */}
          <div className="px-6 pt-6 pb-4 border-b border-border-default">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-accent-gold/20 flex items-center justify-center">
                <PdfIcon className="w-5 h-5 text-accent-gold" />
              </div>
              <div>
                <h2
                  id="export-dialog-title"
                  className="text-lg font-semibold text-text-primary"
                >
                  Export Character Sheet
                </h2>
                <p className="text-sm text-text-muted">
                  {characterName || 'Unnamed Character'}
                </p>
              </div>
            </div>
          </div>

          {/* Template Selection */}
          <div className="px-6 py-5">
            <p className="text-sm text-text-secondary mb-4">
              Choose a template style for your character sheet:
            </p>

            <div className="grid grid-cols-1 gap-3">
              {availableTemplates.map((template) => (
                <button
                  key={template.id}
                  type="button"
                  onClick={() => setSelectedTemplateId(template.id)}
                  disabled={isGenerating}
                  className={clsx(
                    'w-full rounded-lg border-2 text-left transition-all overflow-hidden',
                    selectedTemplateId === template.id
                      ? 'border-accent-gold bg-accent-gold/10'
                      : 'border-border-default hover:border-border-hover bg-background-secondary',
                    isGenerating && 'opacity-50 cursor-not-allowed'
                  )}
                >
                  <div className="flex gap-4 p-4">
                    {/* Thumbnail Preview */}
                    <div className={clsx(
                      'flex-shrink-0 w-24 h-32 rounded border overflow-hidden',
                      'flex items-center justify-center',
                      selectedTemplateId === template.id
                        ? 'border-accent-gold/50 bg-background-elevated'
                        : 'border-border-default bg-background-primary'
                    )}>
                      <TemplateThumbnail theme={template.theme} />
                    </div>

                    {/* Template Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-medium text-text-primary">
                          {template.displayName}
                        </span>
                        {template.id === defaultTemplate?.id && (
                          <span className="px-2 py-0.5 rounded text-xs bg-accent-gold/20 text-accent-gold">
                            Default
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-text-muted mb-2">
                        {template.description}
                      </p>

                      {/* Template Features */}
                      <div className="flex flex-wrap gap-1 text-xs text-text-muted">
                        {getTemplateFeatures(template).map((feature, idx) => (
                          <span key={idx} className="px-2 py-0.5 rounded bg-background-elevated">
                            {feature}
                          </span>
                        ))}
                      </div>
                    </div>

                    {/* Selection Indicator */}
                    <div className="flex-shrink-0 flex items-start pt-1">
                      <div
                        className={clsx(
                          'w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors',
                          selectedTemplateId === template.id
                            ? 'border-accent-gold bg-accent-gold'
                            : 'border-border-default'
                        )}
                      >
                        {selectedTemplateId === template.id && (
                          <CheckIcon className="w-3 h-3 text-background-primary" />
                        )}
                      </div>
                    </div>
                  </div>
                </button>
              ))}
            </div>

            {/* Template Info */}
            {selectedTemplate && (
              <div className="mt-4 p-3 rounded-lg bg-background-elevated text-sm">
                <div className="flex items-center gap-2 text-text-muted">
                  <InfoIcon className="w-4 h-4" />
                  <span>
                    {selectedTemplate.supportsIgnusFields
                      ? 'Includes Ignus-specific fields (faction, lineage traits, elemental nature)'
                      : 'Standard D&D 5e character sheet fields'}
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="px-6 pb-6 flex gap-3">
            <button
              type="button"
              onClick={onClose}
              disabled={isGenerating}
              className={clsx(
                'flex-1 px-4 py-2.5 rounded-lg border border-border-default font-medium transition-colors',
                isGenerating
                  ? 'text-text-muted cursor-not-allowed'
                  : 'text-text-secondary hover:bg-background-elevated'
              )}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleExport}
              disabled={isGenerating || !selectedTemplateId}
              className={clsx(
                'flex-1 px-4 py-2.5 rounded-lg font-medium transition-colors flex items-center justify-center gap-2',
                isGenerating || !selectedTemplateId
                  ? 'bg-accent-gold/50 text-background-primary/50 cursor-not-allowed'
                  : 'bg-accent-gold text-background-primary hover:bg-accent-gold/90'
              )}
            >
              {isGenerating ? (
                <>
                  <LoadingSpinner className="w-4 h-4" />
                  Generating...
                </>
              ) : (
                <>
                  <DownloadIcon className="w-4 h-4" />
                  Export PDF
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

function PdfIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 13h6m-6 4h4" />
    </svg>
  );
}

function CheckIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
    </svg>
  );
}

function InfoIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  );
}

function DownloadIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
    </svg>
  );
}

function LoadingSpinner({ className }: { className?: string }) {
  return (
    <svg className={clsx(className, 'animate-spin')} fill="none" viewBox="0 0 24 24">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      />
    </svg>
  );
}
